import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, API, APP_TYPE_LABELS, INTERNAL_STAGE_LABELS, fmtDDMMYYYY } from "../../api";
import { PageHeader, Panel, Btn, Input, Select, Empty, Pill } from "../../ui";

export default function ApplicationsPage() {
  const [list, setList] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [properties, setProperties] = useState([]);
  const [show, setShow] = useState(false);
  const [f, setF] = useState({ type: "res_mortgage", subtype: "purchase", customer_id: "", business_id: "", property_id: "" });

  const load = async () => {
    const [a, b, c, d] = await Promise.all([
      api.get(`${API}/applications`),
      api.get(`${API}/customers`),
      api.get(`${API}/businesses`),
      api.get(`${API}/properties`),
    ]);
    setList(a.data); setCustomers(b.data); setBusinesses(c.data); setProperties(d.data);
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      const payload = { type: f.type, subtype: f.type.endsWith("mortgage") ? f.subtype : null,
        customer_id: f.customer_id || null, business_id: f.business_id || null, property_id: f.property_id || null };
      await api.post(`${API}/applications`, payload);
      toast.success("Case created");
      setShow(false);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not create"); }
  };

  return (
    <div>
      <PageHeader title="Applications" subtitle="Every case has a unique reference, a case owner, a next action and a follow-up date." action={<Btn onClick={() => setShow(true)} data-testid="apps-new">+ New application</Btn>} />
      {show && (
        <Panel className="p-6 mb-6" >
          <form onSubmit={create} className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="apps-new-form">
            <Select label="What do you need help with?" value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })} testid="apps-type">
              {Object.entries(APP_TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </Select>
            {(f.type === "res_mortgage" || f.type === "btl_mortgage") && (
              <Select label="Purchase or remortgage?" value={f.subtype} onChange={(e) => setF({ ...f, subtype: e.target.value })} testid="apps-subtype">
                <option value="purchase">Purchase</option>
                <option value="remortgage">Remortgage</option>
              </Select>
            )}
            <Select label="Customer" value={f.customer_id} onChange={(e) => setF({ ...f, customer_id: e.target.value })} testid="apps-customer">
              <option value="">— Pick customer —</option>
              {customers.map((c) => <option key={c.id} value={c.id}>{c.primary_applicant?.name}</option>)}
            </Select>
            {f.type === "business_loan" && (
              <Select label="Business" value={f.business_id} onChange={(e) => setF({ ...f, business_id: e.target.value })} testid="apps-business">
                <option value="">— Pick business —</option>
                {businesses.map((b) => <option key={b.id} value={b.id}>{b.legal_name}</option>)}
              </Select>
            )}
            {(f.type === "res_mortgage" || f.type === "btl_mortgage") && (
              <Select label="Property (optional)" value={f.property_id} onChange={(e) => setF({ ...f, property_id: e.target.value })} testid="apps-property">
                <option value="">— None yet —</option>
                {properties.map((p) => <option key={p.id} value={p.id}>{p.address}</option>)}
              </Select>
            )}
            <div className="sm:col-span-2 flex justify-end gap-2 pt-2">
              <Btn variant="ghost" type="button" onClick={() => setShow(false)}>Cancel</Btn>
              <Btn type="submit" data-testid="apps-save">Create case</Btn>
            </div>
          </form>
        </Panel>
      )}
      {list === null && <p className="text-sm text-slate-500">Loading…</p>}
      {list && list.length === 0 && <Empty title="No cases yet" />}
      {list && list.length > 0 && (
        <Panel>
          <table className="w-full text-sm">
            <thead><tr className="border-b border-slate-200 text-[10.5px] tracking-[0.14em] uppercase text-slate-500 bg-slate-50"><th className="text-left px-5 py-3">Ref</th><th className="text-left px-5 py-3">Type</th><th className="text-left px-5 py-3">Stage</th><th className="text-left px-5 py-3">Follow-up</th><th className="text-left px-5 py-3">Next action</th></tr></thead>
            <tbody>
              {list.map((a, i) => (
                <tr key={a.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`app-row-${i+1}`}>
                  <td className="px-5 py-3 font-mono"><Link to={`/agency/applications/${a.id}`} className="text-slate-800 font-semibold hover:underline" data-testid={`app-open-${i+1}`}>{a.ref}</Link></td>
                  <td className="px-5 py-3 text-slate-600">{APP_TYPE_LABELS[a.type]}{a.subtype && ` · ${a.subtype}`}</td>
                  <td className="px-5 py-3"><Pill tone="sky">{INTERNAL_STAGE_LABELS[a.internal_status] || a.internal_status}</Pill></td>
                  <td className="px-5 py-3 text-slate-600">{fmtDDMMYYYY(a.follow_up_date)}</td>
                  <td className="px-5 py-3 text-slate-700 text-[12px]">{a.next_action || "—"} <span className="text-slate-500">· {a.next_action_owner}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </Panel>
      )}
    </div>
  );
}
